// /sync — Sync issues + pre-show readiness. Reached by tapping the app-bar sync
// badge, and the place a stuck lead is recovered (docs/plan.md, "When a queued
// lead cannot be sent").
//
// A queued lead that the server permanently rejected (VALIDATION_ERROR /
// CONFLICT) is the app's only silent data-loss path: it presents as success and
// no other screen lists it. This screen is the route back — Retry, Edit and
// retry, or Discard (behind a confirm that names what is destroyed). Nothing is
// ever auto-discarded.
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { ChevronLeft, RotateCcw } from 'lucide-react';
import { useState } from 'react';

import type { OutboxRow, OutboxStatus } from '@/lib/db';
import { discardOutboxEntry, listOutbox, retryOutboxEntry, setResumeTarget, useIsOffline } from '@/lib/offline';
import { reinstallApp } from '@/pwa/reinstall-app';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { ReadinessPanel } from '@/components/offline/readiness-panel';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export const Route = createFileRoute('/_app/sync')({
  staticData: { barSubtitle: 'Sync issues', showEventSelect: false },
  component: SyncScreen
});

const STATUS_LABEL: Record<OutboxStatus, string> = {
  pending: 'Waiting to send',
  in_flight: 'Sending…',
  failed_transient: 'Retrying automatically',
  failed_permanent: 'Needs attention',
  done: 'Sent'
};

function SyncScreen() {
  // Live — the list re-renders as the engine drains, retries and fails entries.
  const entries = useLiveQuery(() => listOutbox(), [], [] as OutboxRow[]);
  const router = useRouter();
  const navigate = useNavigate();

  const stuck = entries.filter(e => e.status === 'failed_permanent');
  const queued = entries.filter(e => e.status !== 'failed_permanent');

  /**
   * This screen is entered from the app-bar sync badge on ANY screen, so
   * "back" means the screen they left. Falls back to the board when there is
   * no history to pop — a reload or a bookmark straight into `/sync` would
   * otherwise leave the button dead, or worse, walk out of the app.
   */
  function handleBack(): void {
    if (router.history.canGoBack()) {
      router.history.back();
      return;
    }
    void navigate({ to: '/' });
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-6">
      <div>
        <Button variant="ghost" size="sm" onClick={handleBack} className="-ml-2">
          <ChevronLeft />
          Back
        </Button>
      </div>

      <ReadinessPanel />

      <Card>
        <CardHeader>
          <CardTitle>Captured leads waiting to sync</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {entries.length === 0 && (
            <p className="text-muted-foreground text-sm">Everything captured on this device has synced.</p>
          )}

          {stuck.length > 0 && (
            <section className="flex flex-col gap-3">
              <h3 className="text-destructive text-xs font-semibold tracking-wide uppercase">
                {stuck.length} could not be sent
              </h3>
              {stuck.map(entry => (
                <StuckRow key={entry.clientUuid} entry={entry} />
              ))}
            </section>
          )}

          {queued.length > 0 && (
            <section className="flex flex-col gap-3">
              <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                {queued.length} in progress
              </h3>
              {queued.map(entry => (
                <QueuedRow key={entry.clientUuid} entry={entry} />
              ))}
            </section>
          )}
        </CardContent>
      </Card>

      <RecoveryCard />
    </div>
  );
}

/**
 * The way out of a stale or stuck app.
 *
 * `reinstallApp()` throws away the cached PROGRAM and reloads, keeping both the
 * captured leads (IndexedDB) and the downloaded product photos
 * (`catalogue-images-v1`) — see that module for why those two are the keep
 * list. That is what lets the copy below promise so much, and it is worth
 * keeping the promise accurate if the keep list ever changes.
 *
 * WHY IT IS BLOCKED OFFLINE — not the reason it used to be. This once linked to
 * a standalone `/recover.html`, unreachable offline because the SW served it
 * NetworkOnly. The work now happens in-app, so it WOULD run offline, and that
 * is the hazard: it removes the only copy of the app on the device and the
 * reload would have nothing to fetch, leaving a rep with no app mid-show.
 * Refusing without a connection is the safety property, not a limitation.
 */
function RecoveryCard() {
  const isOffline = useIsOffline();
  const [confirming, setConfirming] = useState(false);

  return (
    <Card>
      <CardHeader>
        <CardTitle>App not behaving?</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-muted-foreground text-sm">
          Downloads a fresh copy of the app — the fix for a screen that is stuck, blank or out of date.{' '}
          <strong className="text-foreground font-medium">
            Your buyers and downloaded product photos stay on this device.
          </strong>
        </p>
        <Button variant="outline" className="self-start" disabled={isOffline} onClick={() => setConfirming(true)}>
          <RotateCcw />
          Reinstall app
        </Button>
        {isOffline && (
          <p className="text-muted-foreground text-xs">
            Needs a connection — the app is downloaded again, so doing this with no signal would leave nothing to
            reload.
          </p>
        )}
      </CardContent>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Reinstall the app?"
        description="A fresh copy is downloaded and the screen reloads. Buyers waiting to sync and your downloaded product photos are kept."
        confirmLabel="Reinstall"
        onConfirm={() => {
          void reinstallApp();
        }}
      />
    </Card>
  );
}

function capturedAt(ms: number): string {
  return new Date(ms).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function QueuedRow({ entry }: { entry: OutboxRow }) {
  return (
    <div className="border-border flex flex-col gap-0.5 rounded-lg border p-3">
      <div className="text-sm font-medium">{entry.company}</div>
      <div className="text-muted-foreground text-xs">
        {STATUS_LABEL[entry.status]} · captured {capturedAt(entry.createdAt)}
      </div>
    </div>
  );
}

function StuckRow({ entry }: { entry: OutboxRow }) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);

  function editAndRetry() {
    // Hand the capture form the entry to prefill; it discards this one on resubmit.
    setResumeTarget(entry.clientUuid);
    void navigate({ to: '/leads/new' });
  }

  return (
    <div className="border-destructive/30 bg-destructive/5 flex flex-col gap-2 rounded-lg border p-3">
      <div>
        <div className="text-sm font-medium">{entry.company}</div>
        <div className="text-muted-foreground text-xs">Captured {capturedAt(entry.createdAt)}</div>
      </div>
      {entry.lastError && (
        <p className="text-destructive text-xs">
          {entry.lastErrorCode ? `${entry.lastErrorCode}: ` : ''}
          {entry.lastError}
        </p>
      )}

      {confirming ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm">
            Discard the captured lead for <span className="font-medium">{entry.company}</span>? This cannot be undone —
            the lead is not stored anywhere else.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button size="default" variant="outline" onClick={() => setConfirming(false)}>
              Keep it
            </Button>
            <Button size="default" variant="destructive" onClick={() => void discardOutboxEntry(entry.clientUuid)}>
              Discard permanently
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button size="default" variant="outline" onClick={() => void retryOutboxEntry(entry.clientUuid)}>
            Retry
          </Button>
          <Button size="default" variant="outline" onClick={editAndRetry}>
            Edit and retry
          </Button>
          <Button size="default" variant="destructive" onClick={() => setConfirming(true)}>
            Discard
          </Button>
        </div>
      )}
    </div>
  );
}
